import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { PickedForYouSection } from "@/components/PickedForYouSection";
import { PersonCard } from "@/components/PersonCard";
import { ArenaPartnerCircleCard } from "@/components/ArenaPartnerCircleCard";
import { ArenaPhotoReminder } from "@/components/ArenaPhotoReminder";
import { HostedCirclesProvider } from "@/lib/hosted-circles";
import { buildContactMessage } from "@/lib/contact-message";
import { ResidentProfileView } from "@/components/ResidentProfileView";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { Profile } from "@/lib/people-constants";
import { ContactButtons, MY_CONTACT_KEY } from "@/lib/coffee-contact";
import { InviteToCoffeeDialog } from "@/components/InviteToCoffeeDialog";
import { supabase } from "@/integrations/supabase/client";
import { isWhatsappLink, isInstagramLink } from "@/lib/whatsapp";
import { arenaSteps } from "@/lib/onboarding-steps";
import { factsFromProfile } from "@/lib/onboarding-facts";
import { districtsFor } from "@/lib/districts";
import { ArenaSeekingReminder } from "@/components/ArenaSeekingReminder";
import { matchScore } from "@/lib/match-people";
import { ARENA_COPY, arenaErrorText } from "@/lib/arena-copy";
import {
  countOpenedOpportunities,
  inviteStateFor,
  showArenaContacts,
  type CoffeeInviteRow,
  milestoneProgress,
  arenaTaskView,
  arenaBannerMode,
  nextMilestoneLine,
  sortAcquaintedLast,
  pendingForMeIds,
  formatArenaDays,
  matchesDayFilter,
  defaultDayFilter,
  isHereToday,
  type ArenaDayFilter,
  arenaSearchMatch,
  isArenaProfileComplete,
} from "@/lib/arena-logic";
import {
  arenaMeKey,
  arenaRpc,
  fetchArenaMe,
  useArenaCount,
  useArenaMe,
  type ArenaMeeting,
  type ArenaPerson,
} from "@/lib/arena";

const TITLE = "Свои на ARENA — участники фестиваля";
const DESC = "Участники ARENA, с которыми стоит познакомиться прямо на фестивале.";

export const Route = createFileRoute("/_authenticated/arena/people")({
  beforeLoad: async ({ context }) => {
    const uid = (context as { user?: { id: string } }).user?.id;
    if (!uid) return;
    let me = null;
    try {
      me = await context.queryClient.ensureQueryData({
        queryKey: arenaMeKey(uid),
        queryFn: () => fetchArenaMe(uid),
        staleTime: 60_000,
      });
    } catch {
      me = null;
    }
    if (!me || (me.days ?? []).length === 0) throw redirect({ to: "/arena" });
    // Профиль не прошёл мастер ARENA — вернуть в мастер (только если мастеру есть что показать).
    let prof = null;
    try {
      const { data } = await supabase.from("profiles").select("*").eq("user_id", uid).maybeSingle();
      prof = data;
    } catch {
      prof = null;
    }
    if (prof && !isArenaProfileComplete(prof)) {
      const steps = arenaSteps(factsFromProfile(prof, districtsFor(prof.city).length > 0));
      if (steps.length > 0) throw redirect({ href: "/profile/setup?mode=arena" });
    }
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ArenaPeoplePage,
});

const firstName = (n: string | null | undefined) =>
  (n ?? "").trim().split(/\s+/)[0] || "собеседника";


function useNow(ms: number) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

function ArenaPeoplePage() {
  const { user } = useAuth();
  const uid = user?.id;
  const { data: myName = null } = useQuery({
    queryKey: ["my-full-name", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("full_name").eq("user_id", uid!).maybeSingle();
      return (data?.full_name as string | null) ?? null;
    },
  });
  const qc = useQueryClient();
  const { data: count } = useArenaCount();
  const { data: me } = useArenaMe();
  const now = useNow(60_000);
  const [dayPick, setDayPick] = useState<ArenaDayFilter | null>(null);
  const dayFilter: ArenaDayFilter = dayPick ?? defaultDayFilter(now);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [inviteTo, setInviteTo] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ArenaPerson | null>(null);

  const { data: invites = [] } = useQuery({
    queryKey: ["arena-invites", uid],
    enabled: !!uid,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coffee_invites")
        .select("id, sender_user_id, recipient_user_id, status, responded_at, created_at")
        .or(`sender_user_id.eq.${uid},recipient_user_id.eq.${uid}`);
      if (error) throw error;
      return (data ?? []) as CoffeeInviteRow[];
    },
  });

  const { data: people = [], isLoading } = useQuery({
    queryKey: ["arena-people"],
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await arenaRpc.people();
      if (error) throw error;
      return (data ?? []) as ArenaPerson[];
    },
  });
  const { data: meetings = [], isSuccess: meetingsLoaded } = useQuery({
    queryKey: ["arena-meetings", uid],
    enabled: !!uid,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await arenaRpc.myMeetings();
      if (error) throw error;
      return (data ?? []) as ArenaMeeting[];
    },
  });

  const self = people.find((p) => p.user_id === uid);
  const myInterests = useMemo(
    () => new Set((self?.interests ?? []).map((i) => i.toLowerCase())),
    [self],
  );
  const meetingWith = useMemo(() => {
    const m = new Map<string, ArenaMeeting>();
    for (const x of meetings) m.set(x.user_a === uid ? x.user_b : x.user_a, x);
    return m;
  }, [meetings, uid]);
  const completed = countOpenedOpportunities(meetings);
  const pendingIds = useMemo(() => pendingForMeIds(meetings, uid), [meetings, uid]);
  const pendingSet = useMemo(() => new Set(pendingIds), [pendingIds]);

  // Тост о завершении встречи, которую подтвердил второй человек: один раз на встречу.
  const seenInit = useRef(false);
  useEffect(() => {
    if (!uid || !meetingsLoaded) return;
    const key = `arenaDoneSeen:${uid}`;
    let seen: string[] = [];
    try {
      seen = JSON.parse(sessionStorage.getItem(key) ?? "[]");
    } catch {
      seen = [];
    }
    const done = meetings.filter((m) => m.completed_at).map((m) => m.id);
    const fresh = done.filter((id) => !seen.includes(id));
    const first = !seenInit.current;
    seenInit.current = true;
    if (fresh.length === 0) return;
    if (!first) toast.success(done.length > 1 ? ARENA_COPY.countToast(done.length) : ARENA_COPY.completedToast);
    try {
      sessionStorage.setItem(key, JSON.stringify([...seen, ...fresh]));
    } catch {
      /* ignore */
    }
  }, [meetings, uid, meetingsLoaded]);

  const ranked = useMemo(
    () =>
      people
        .filter((p) => p.user_id !== uid)
        .map((p) => ({ p, ...matchScore(self, p, { arena: true }) }))
        .sort((a, b) => b.score - a.score || Number(!!b.p.photo_url) - Number(!!a.p.photo_url)),
    [people, self, uid],
  );

  const chips = useMemo(() => {
    const freq = new Map<string, number>();
    for (const { p } of ranked)
      for (const i of p.interests ?? []) freq.set(i, (freq.get(i) ?? 0) + 1);
    return [...freq.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([k]) => k);
  }, [ranked]);

  const stateOf = (id: string) => inviteStateFor(invites, uid, id);
  const incomingIds = ranked
    .filter(({ p }) => stateOf(p.user_id).state === "incoming")
    .map(({ p }) => p.user_id);
  const incomingSet = new Set(incomingIds);
  const urgent = (id: string) => pendingSet.has(id) || incomingSet.has(id);

  const list = ranked.filter(({ p }) => {
    const pending = urgent(p.user_id);
    if (!pending && !matchesDayFilter(p.days, dayFilter)) return false;
    if (!arenaSearchMatch(p, q)) return false;
    if (chip && !(p.interests ?? []).includes(chip)) return false;
    return true;
  });
  list.sort((a, b) => Number(urgent(b.p.user_id)) - Number(urgent(a.p.user_id)));
  const isAcq = (id: string) => !!meetingWith.get(id)?.completed_at;
  const orderedList = sortAcquaintedLast(list, ({ p }) => isAcq(p.user_id));
  const hasNewPeople = ranked.some(({ p }) => !isAcq(p.user_id));
  const taskView = me ? arenaTaskView(completed, me.sticker_given_at) : "none";
  const bannerMode = arenaBannerMode(completed, me?.sticker_given_at);

  function showPending(ids: string[] = pendingIds) {
    setQ("");
    setChip(null);
    const id = ids[0];
    window.setTimeout(() => {
      document
        .getElementById(`arena-person-${id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }

  async function respond(inviteId: string, accept: boolean) {
    setBusyId(inviteId);
    const { error } = await supabase.rpc("respond_to_coffee_invite", {
      p_id: inviteId,
      p_accept: accept,
    });
    setBusyId(null);
    if (error) {
      if ((error as { hint?: string }).hint === "no_contact") {
        toast.error(ARENA_COPY.inviteNoContact);
        qc.invalidateQueries({ queryKey: MY_CONTACT_KEY });
      } else toast.error(error.message || ARENA_COPY.errors.generic);
      return;
    }
    toast.success(accept ? ARENA_COPY.inviteAcceptedToast : ARENA_COPY.inviteDeclinedToast);
    qc.invalidateQueries({ queryKey: ["arena-invites", uid] });
    qc.invalidateQueries({ queryKey: ["arena-people"] });
    qc.invalidateQueries({ queryKey: ["coffee-invites"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  }

  async function confirm(other: ArenaPerson) {
    setBusyId(other.user_id);
    const { data, error } = await arenaRpc.confirm(other.user_id);
    setBusyId(null);
    if (error) return toast.error(arenaErrorText(error));
    const row = (data as { completed: boolean }[] | null)?.[0];
    toast.success(row?.completed ? (completed >= 1 ? ARENA_COPY.countToast(completed + 1) : ARENA_COPY.completedToast) : ARENA_COPY.confirmed);
    if (row?.completed && uid) {
      const mt = meetingWith.get(other.user_id);
      if (mt) {
        try {
          const key = `arenaDoneSeen:${uid}`;
          const seen: string[] = JSON.parse(sessionStorage.getItem(key) ?? "[]");
          sessionStorage.setItem(key, JSON.stringify([...seen, mt.id]));
        } catch {
          /* ignore */
        }
      }
    }
    qc.invalidateQueries({ queryKey: ["arena-meetings", uid] });
    qc.invalidateQueries({ queryKey: ["resident-contribution", uid] });
  }

  return (
    <div className="min-h-screen w-full overflow-x-clip bg-white">
      <HostedCirclesProvider userIds={list.map(({ p }) => p.user_id)}>
      <main className="mx-auto w-full max-w-2xl space-y-3 px-4 py-6">
        <header className="flex items-center gap-3">
          <MrDriveAvatar variant="people" size={56} />
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold text-td-dark">{ARENA_COPY.title}</h1>
            {typeof count === "number" && (
              <p className="text-sm font-semibold text-td-teal">{ARENA_COPY.counter(count)}</p>
            )}
          </div>
        </header>

        <section className="rounded-2xl bg-gradient-to-r from-td-orange/15 to-td-teal/10 px-4 py-3">
          <p className="text-sm font-semibold text-td-dark">
            {bannerMode === "continue" ? ARENA_COPY.continueTitle : ARENA_COPY.task}
          </p>
          {bannerMode === "task" && <p className="mt-1 text-xs text-td-dark/80">{ARENA_COPY.taskSteps}</p>}
          <Opportunities n={completed} uid={uid} continueMode={bannerMode === "continue"} />
        </section>

        {incomingIds.length > 0 && (
          <CompactBanner text={ARENA_COPY.inviteBanner(incomingIds.length)} onShow={() => showPending(incomingIds)} />
        )}
        {pendingIds.length > 0 && (
          <CompactBanner text={ARENA_COPY.pendingBanner(pendingIds.length)} onShow={() => showPending()} />
        )}

        {taskView === "claim" && <TaskDone />}
        {taskView === "next" && me?.sticker_given_at && (
          <StickerLine stickerAt={me.sticker_given_at} hasNewPeople={hasNewPeople} />
        )}


        <ArenaPhotoReminder uid={uid} isParticipant={!!me} />
        {self && <ArenaSeekingReminder uid={uid} self={self} />}

        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-td-muted" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={ARENA_COPY.searchPlaceholder}
              className="h-11 pl-9 text-base"
            />
          </div>
          {chips.length > 0 && (
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
              {chips.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setChip(chip === c ? null : c)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${chip === c ? "border-td-teal bg-td-teal text-white" : "border-td-line bg-white text-td-dark"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2" role="group" aria-label="День фестиваля">
            {(["all", 24, 25] as ArenaDayFilter[]).map((d) => (
              <button
                key={String(d)}
                type="button"
                aria-pressed={dayFilter === d}
                onClick={() => setDayPick(d)}
                className={`rounded-full border px-3 py-1.5 text-sm ${dayFilter === d ? "border-td-teal bg-td-teal text-white" : "border-td-line bg-white text-td-dark"}`}
              >
                {d === "all" ? ARENA_COPY.dayAll : `${d} окт`}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-td-muted">Подбираю своих…</p>
        ) : list.length === 0 ? (
          <p className="py-6 text-center text-sm text-td-muted">
            {ranked.length === 0 ? ARENA_COPY.empty : ARENA_COPY.emptyFiltered}
          </p>
        ) : (
          <div id="arena-picked" className="scroll-mt-24">
          <PickedForYouSection title={ARENA_COPY.pickedForYou}>
            {orderedList.map(({ p, reasons }) => {
              const m = meetingWith.get(p.user_id);
              const iAmA = m ? m.user_a === uid : false;
              const mine = m ? (iAmA ? m.confirmed_by_a : m.confirmed_by_b) : null;
              const daysLabel = formatArenaDays(p.days);
              const hereToday = isHereToday(p.days, now);
              const pendingMe = pendingSet.has(p.user_id);
              const { state, invite } = stateOf(p.user_id);
              const incoming = state === "incoming";
              const links = (p.social_links ?? []).filter(
                (l) => !!l?.trim() && (isWhatsappLink(l) || isInstagramLink(l) || /t\.me\//i.test(l)),
              );
              const hasContact =
                showArenaContacts(state) && (!!p.telegram?.trim() || links.length > 0);
              const hasShared = (p.interests ?? []).some((i) => myInterests.has(i.toLowerCase()));
              const other = reasons.find((r) => r.kind !== "interests");
              const reasonText = hasShared
                ? null
                : other
                  ? other.kind === "help"
                      ? ARENA_COPY.reasonHelp(other.text)
                      : ARENA_COPY.reasonIndustry(other.text)
                  : ARENA_COPY.reasonDefault;
              const stop = (e: React.SyntheticEvent) => e.stopPropagation();
              const name = firstName(p.full_name);

              const action = null;
              const row = "mt-3 flex flex-wrap items-center justify-end gap-2";
              const btn =
                "inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-50";

              const header =
                !incoming && pendingMe ? (
                  <p className="mb-1.5 text-sm font-semibold text-td-orange">{ARENA_COPY.pendingForMe(name)}</p>
                ) : null;

              const footer =
                m?.completed_at ? (
                  <div className={row} onClick={stop} onKeyDown={stop}>
                    <span className={`bg-td-teal/10 text-td-teal ${btn}`} aria-disabled="true">
                      {ARENA_COPY.acquainted}
                    </span>
                  </div>
                ) : state === "none" ? (
                  <div className={row} onClick={stop} onKeyDown={stop}>
                    <button
                      type="button"
                      onClick={() => setInviteTo(p.user_id)}
                      aria-label={`${ARENA_COPY.inviteCta}: ${p.full_name}`}
                      className={`border border-td-orange/40 bg-td-orange/12 text-accent-900 ${btn}`}
                    >
                      <span aria-hidden="true" className="text-[16px] leading-none">☕</span>
                      {ARENA_COPY.inviteCta}
                    </button>
                  </div>
                ) : state === "sent" ? (
                  <div className={row} onClick={stop} onKeyDown={stop}>
                    <span className={`bg-td-teal/10 text-td-teal ${btn}`} aria-disabled="true">
                      {ARENA_COPY.inviteSentCta}
                    </span>
                  </div>
                ) : incoming && invite ? (
                  <div className={row} onClick={stop} onKeyDown={stop}>
                    <p className="w-full text-sm font-medium text-td-dark">{ARENA_COPY.inviteCalls(name)}</p>
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        disabled={busyId === invite.id}
                        onClick={() => respond(invite.id, true)}
                        className={`cta-solid ${btn}`}
                      >
                        {ARENA_COPY.inviteAccept}
                      </button>
                      <button
                        type="button"
                        disabled={busyId === invite.id}
                        onClick={() => respond(invite.id, false)}
                        className={`border border-td-line bg-white text-td-dark ${btn}`}
                      >
                        {ARENA_COPY.inviteDecline}
                      </button>
                    </div>
                  </div>
                ) : state === "declined" ? (
                  <p className="mt-2 text-xs text-td-muted">{ARENA_COPY.inviteDeclined}</p>
                ) : state === "accepted" ? (
                  <div className={row} onClick={stop} onKeyDown={stop}>
                    {hasContact && <ContactButtons telegram={p.telegram} links={links} message={buildContactMessage({ context: "arena", recipientName: p.full_name, senderName: myName })} buttonClassName="h-10 rounded-full px-4 text-sm font-semibold" />}
                    {m?.completed_at ? (
                      <span className="text-sm font-semibold text-td-teal">{ARENA_COPY.metDone}</span>
                    ) : mine ? (
                      <span className="text-sm text-td-muted">{ARENA_COPY.waiting(name)}</span>
                    ) : (
                      <button
                        type="button"
                        disabled={busyId === p.user_id}
                        onClick={() => confirm(p)}
                        className={`cta-solid ${btn}`}
                      >
                        {pendingMe ? ARENA_COPY.confirmCta : ARENA_COPY.met}
                      </button>
                    )}
                  </div>
                ) : null;

              return (
                <PersonCard
                  key={p.user_id}
                  id={`arena-person-${p.user_id}`}
                  p={p as unknown as Profile}
                  myInterests={myInterests}
                  showSharedNames
                  onInvite={() => setInviteTo(p.user_id)}
                  onOpen={() => setViewing(p)}
                  highlight={pendingMe || incoming}
                  action={action}
                  header={header}
                  footer={footer}
                  badges={
                    daysLabel || state === "accepted" ? (
                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        {daysLabel && (
                          <span className="inline-flex w-fit items-center rounded-full bg-td-teal/10 px-2 py-0.5 text-[10px] font-medium text-td-teal">
                            {daysLabel}
                            {hereToday && ` · ${ARENA_COPY.hereToday}`}
                          </span>
                        )}
                        {state === "accepted" && !m?.completed_at && (
                          <span className="text-[11px] font-medium text-td-teal">{ARENA_COPY.inviteAgreed}</span>
                        )}
                      </div>
                    ) : null
                  }
                  afterProfession={
                    p.looking_for?.trim() ? (
                      <p className="mt-0.5 line-clamp-3 break-words text-xs text-td-dark">
                        <span className="font-semibold">{ARENA_COPY.lookingLabel}</span>{" "}
                        {p.looking_for.trim()}
                      </p>
                    ) : null
                  }
                  extraLine={
                    reasonText ? (
                      <span className="mt-0.5 block truncate text-[11px] text-td-teal">{reasonText}</span>
                    ) : null
                  }
                />
              );
            })}
          </PickedForYouSection>
          </div>
        )}
        <ArenaPartnerCircleCard />
      </main>
      </HostedCirclesProvider>
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto p-0 sm:max-w-lg">
          {viewing && (
            <>
              <DialogTitle className="sr-only">{viewing.full_name}</DialogTitle>
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="m-3 mb-0 h-10 rounded-full px-3 text-sm font-semibold text-td-teal"
              >
                {ARENA_COPY.backToArena}
              </button>
              <ArenaProfile person={viewing} />
            </>
          )}
        </DialogContent>
      </Dialog>
      <InviteToCoffeeDialog
        open={!!inviteTo}
        onOpenChange={(v) => !v && setInviteTo(null)}
        recipientUserId={inviteTo ?? undefined}
        initialMessage={ARENA_COPY.inviteMessage}
        onSent={() => qc.invalidateQueries({ queryKey: ["arena-invites", uid] })}
      />
    </div>
  );
}

const fmtSticker = (d: Date) =>
  d.toLocaleString("ru-RU", {
    timeZone: "Europe/Istanbul",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

function StickerLine({ stickerAt, hasNewPeople }: { stickerAt: string; hasNewPeople: boolean }) {
  return (
    <div className="space-y-1">
      <p className="flex flex-wrap items-center gap-x-1 text-xs font-medium text-td-teal">
        <Check className="size-3.5" aria-hidden="true" />
        {ARENA_COPY.stickerDone} · <span className="text-td-dark/80">{fmtSticker(new Date(stickerAt))}</span>
      </p>
      {!hasNewPeople && <p className="text-sm text-td-dark">{ARENA_COPY.nextNoOne}</p>}
    </div>
  );
}

function TaskDone() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const now = useNow(1000);
  const [busy, setBusy] = useState(false);
  const fmt = (d: Date) =>
    d.toLocaleString("ru-RU", {
      timeZone: "Europe/Istanbul",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  async function claim() {
    setBusy(true);
    const { error } = await arenaRpc.claimSticker();
    setBusy(false);
    if (error) toast.error(arenaErrorText(error));
    qc.invalidateQueries({ queryKey: arenaMeKey(user?.id) });
  }

  return (
    <section className="overflow-hidden rounded-2xl border-2 border-td-orange bg-white p-5 text-center">
      <div className="mx-auto flex size-16 animate-bounce items-center justify-center rounded-full bg-td-orange/15 text-3xl">
        🐾
      </div>
      <p className="mt-2 font-display text-2xl font-bold text-td-dark">
        {ARENA_COPY.taskDoneTitle}
      </p>
      <p className="mt-2 font-mono text-3xl font-bold tabular-nums text-td-orange">{fmt(now)}</p>
      <p className="mt-2 text-sm text-td-muted">{ARENA_COPY.taskDoneText}</p>
      <button
        type="button"
        disabled={busy}
        onClick={claim}
        className="cta-solid mt-4 h-12 w-full rounded-full text-base font-semibold disabled:opacity-50"
      >
        {ARENA_COPY.stickerCta}
      </button>
    </section>
  );
}

function Opportunities({ n, uid, continueMode = false }: { n: number; uid: string | undefined; continueMode?: boolean }) {
  const p = milestoneProgress(n);
  const label = p.reached === null ? null : ARENA_COPY.milestones[p.reached];
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    if (!uid || p.reached === null) return;
    const key = `arenaMilestone:${uid}`;
    let seen = 0;
    try {
      seen = Number(localStorage.getItem(key) ?? 0);
      localStorage.setItem(key, String(p.reached));
    } catch {
      return;
    }
    if (p.reached > seen && label) {
      setFresh(true);
      toast.success(label);
      const t = window.setTimeout(() => setFresh(false), 1200);
      return () => window.clearTimeout(t);
    }
  }, [p.reached, uid, label]);
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-xs font-semibold text-td-dark">{ARENA_COPY.opportunities(n)}</span>
        {p.next !== null && (
          <span className="shrink-0 text-xs text-td-muted">
            {ARENA_COPY.progressOf(p.done, p.total)}
          </span>
        )}
      </div>
      {p.next !== null && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/70">
          <div
            className="h-full rounded-full bg-td-teal transition-[width] duration-500"
            style={{ width: `${Math.min(100, (p.done / p.total) * 100)}%` }}
          />
        </div>
      )}
      {n === 0 && <p className="text-xs font-medium text-td-teal">{ARENA_COPY.firstSticker}</p>}
      {n > 0 && continueMode && (
        <p className="text-xs font-medium text-td-teal">
          {nextMilestoneLine(n, ARENA_COPY.milestones) ?? ARENA_COPY.nextAllDone}
        </p>
      )}
      {n > 0 && !continueMode && label && (
      <p
        className={`text-xs font-medium text-td-teal transition-all duration-500 ${fresh ? "scale-105 animate-in fade-in slide-in-from-bottom-1" : ""}`}
      >
        {label}
      </p>
      )}
    </div>
  );
}

function CompactBanner({ text, onShow }: { text: string; onShow: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-td-orange/40 bg-td-orange/10 px-3 py-1.5">
      <p className="min-w-0 text-xs font-semibold text-td-dark">{text}</p>
      <button
        type="button"
        onClick={onShow}
        className="shrink-0 rounded-full px-2 py-1 text-xs font-semibold text-td-orange"
      >
        {ARENA_COPY.pendingShow}
      </button>
    </div>
  );
}

/**
 * Профиль участника ARENA. Сначала пробуем полный профиль, как в «Людях».
 * Если каталог его скрывает (visible_in_directory = false), показываем данные,
 * которые участник открыл другим участникам ARENA, — видимость в «Людях» не меняется.
 */
function ArenaProfile({ person }: { person: ArenaPerson }) {
  const { data, isLoading } = useQuery({
    queryKey: ["arena-profile", person.user_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", person.user_id)
        .maybeSingle();
      return (data as unknown as Profile | null) ?? null;
    },
  });
  if (isLoading) return <p className="p-6 text-center text-sm text-td-muted">Открываю профиль…</p>;
  const profile = data ?? ({ ...person, social_links: [], telegram: null } as unknown as Profile);
  return <ResidentProfileView profile={profile} showLookingForText contactContext="arena" />;
}
